/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * The canvas inspector's condition editor: the same groups the classic
 * wizard's ConditionsStep edits ({ groups: [{ id, name, logic, logicMode,
 * customLogic, conditions: [{ id, field, operator, value }] }], groupLogic }),
 * laid out for a 380px panel. Classic keeps ConditionsStep untouched.
 *
 * Text, number, picklist and yes/no values have compact inputs here; dates
 * (with their relative offsets) and lookups reuse the wizard's own inputs.
 */

import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ButtonBase from "@mui/material/ButtonBase";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { Plus, Trash2, X } from "lucide-react";
import { v2 as t, v2Tones } from "styles/tokens/v2";
import DynamicField from "components/AutomationWizard/components/FieldSelector/DynamicField";
import LabeledField from "./LabeledField";

const NO_VALUE = ["is_empty", "is_not_empty", "is_null", "is_not_null"];
const SIMPLE_TYPES = ["text", "textarea", "email", "phone", "url", "number", "currency", "picklist", "boolean"];

const newCondition = () => ({ id: `condition_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`, field: "", operator: "", value: "" });
const newGroup = (n) => ({ id: `group_${Date.now()}`, name: `Condition Group ${n}`, logic: "AND", logicMode: "simple", customLogic: "", conditions: [newCondition()] });

/** "All" / "Any" as a two-button switch. */
function MatchSwitch({ value, onChange, label }) {
  return (
    <Box role="radiogroup" aria-label={label} sx={{ display: "inline-flex", p: "2px", borderRadius: "8px", bgcolor: t.hover, border: `1px solid ${t.border}` }}>
      {[
        ["AND", "All"],
        ["OR", "Any"],
      ].map(([v, text]) => {
        const on = value === v;
        return (
          <ButtonBase
            key={v}
            role="radio"
            aria-checked={on}
            onClick={() => onChange(v)}
            sx={{
              px: 1.25,
              height: 26,
              borderRadius: "6px",
              fontFamily: "inherit",
              fontSize: 13,
              fontWeight: 600,
              color: on ? t.text : t.text2,
              bgcolor: on ? t.surface : "transparent",
              boxShadow: on ? t.shadow : "none",
              "&:focus-visible": { outline: `2px solid ${t.link}`, outlineOffset: 1 },
            }}
          >
            {text}
          </ButtonBase>
        );
      })}
    </Box>
  );
}
MatchSwitch.propTypes = { value: PropTypes.string.isRequired, onChange: PropTypes.func.isRequired, label: PropTypes.string.isRequired };

function ValueInput({ field, operator, value, onChange }) {
  if (NO_VALUE.includes(operator)) {
    return <Typography sx={{ fontSize: 13, color: t.text3, alignSelf: "end", lineHeight: "36px" }}>No value needed</Typography>;
  }
  const common = { label: "Value" };
  const options = field.metadata?.options || field.options || [];
  if (field.type === "picklist" && options.length) {
    return (
      <LabeledField select {...common} value={value || ""} onChange={(e) => onChange(e.target.value)}>
        {options.map((o) => (
          <MenuItem key={o} value={o}>{o}</MenuItem>
        ))}
      </LabeledField>
    );
  }
  if (field.type === "boolean") {
    return (
      <LabeledField select {...common} value={value === true ? "true" : value === false ? "false" : ""} onChange={(e) => onChange(e.target.value === "true")}>
        <MenuItem value="true">Yes</MenuItem>
        <MenuItem value="false">No</MenuItem>
      </LabeledField>
    );
  }
  if (field.type === "number" || field.type === "currency") {
    return <LabeledField {...common} type="number" value={value === "" || value === undefined || value === null ? "" : value} onChange={(e) => onChange(e.target.value === "" ? "" : parseFloat(e.target.value) || 0)} />;
  }
  if (SIMPLE_TYPES.includes(field.type) || !field.type) {
    return <LabeledField {...common} value={value || ""} onChange={(e) => onChange(e.target.value)} />;
  }
  // Dates (absolute or relative) and lookups: the wizard's inputs, sized down.
  return (
    <Box
      sx={{
        width: "100%",
        gridColumn: "1 / -1",
        "& .MuiInputBase-root": { height: "auto", minHeight: 40 },
        "& .MuiSelect-select": { py: "8.5px" },
        "& .MuiInputAdornment-root .MuiSvgIcon-root": { display: "none" },
      }}
    >
      <DynamicField field={field} value={value || ""} onChange={onChange} operator={operator} strictPicklist />
    </Box>
  );
}
ValueInput.propTypes = { field: PropTypes.object.isRequired, operator: PropTypes.string, value: PropTypes.any, onChange: PropTypes.func.isRequired };
ValueInput.defaultProps = { operator: "", value: "" };

function ConditionRow({ condition, fields, loadOperators, onUpdate, onRemove, index }) {
  const [operators, setOperators] = useState([]);
  const field = fields.find((f) => f.id === condition.field);
  const fieldType = field?.type;

  useEffect(() => {
    let cancelled = false;
    if (!fieldType) {
      setOperators([]);
      return undefined;
    }
    Promise.resolve(loadOperators(fieldType))
      .then((ops) => !cancelled && setOperators(ops || []))
      .catch(() => !cancelled && setOperators([]));
    return () => {
      cancelled = true;
    };
  }, [fieldType, loadOperators]);

  return (
    <Box sx={{ position: "relative", p: 1.5, pt: 1.25, borderRadius: "10px", bgcolor: t.surface, border: `1px solid ${t.border}`, display: "flex", flexDirection: "column", gap: 1.25 }}>
      <LabeledField
        select
        label="Field"
        value={condition.field || ""}
        onChange={(e) => onUpdate({ field: e.target.value, operator: "", value: "" })}
        SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 320 } } } }}
      >
        {!fields.length && <MenuItem disabled value="">Choose the record type first</MenuItem>}
        {fields.map((f) => (
          <MenuItem key={f.id} value={f.id}>
            {f.label}
            {f.metadata?.is_custom && <Box component="span" sx={{ ml: 1, fontSize: 11.5, color: t.text3 }}>Custom</Box>}
          </MenuItem>
        ))}
      </LabeledField>
      <Box sx={{ display: "grid", gridTemplateColumns: "minmax(0, 0.9fr) minmax(0, 1.1fr)", gap: 1 }}>
        <LabeledField
          select
          label="Is"
          value={operators.some((o) => o.id === condition.operator) ? condition.operator : ""}
          disabled={!condition.field || !operators.length}
          onChange={(e) => onUpdate({ operator: e.target.value, ...(NO_VALUE.includes(e.target.value) ? { value: "" } : {}) })}
        >
          {operators.map((op) => (
            <MenuItem key={op.id} value={op.id}>{op.name}</MenuItem>
          ))}
        </LabeledField>
        {field && condition.operator ? (
          <ValueInput field={field} operator={condition.operator} value={condition.value} onChange={(value) => onUpdate({ value })} />
        ) : (
          <Box sx={{ mt: "23px", height: 36, borderRadius: "8px", border: `1px dashed ${t.border2}`, display: "grid", placeItems: "center", px: 1 }}>
            <Typography sx={{ fontSize: 12.5, color: t.text3 }}>{condition.field ? "Pick how to compare" : "Pick a field"}</Typography>
          </Box>
        )}
      </Box>
      <Tooltip title="Remove condition">
        <IconButton size="small" aria-label={`Remove condition ${index + 1}`} onClick={onRemove} sx={{ position: "absolute", top: 4, right: 4, color: t.text3, "&:hover": { color: v2Tones.danger.fg, bgcolor: v2Tones.danger.bg } }}>
          <X size={16} />
        </IconButton>
      </Tooltip>
    </Box>
  );
}
ConditionRow.propTypes = {
  condition: PropTypes.object.isRequired,
  fields: PropTypes.arrayOf(PropTypes.object).isRequired,
  loadOperators: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
};

const Joiner = ({ children }) => (
  <Box aria-hidden="true" sx={{ display: "flex", alignItems: "center", gap: 1, my: 0.25 }}>
    <Box sx={{ flex: 1, height: "1px", bgcolor: t.border }} />
    <Typography sx={{ fontSize: 11.5, fontWeight: 700, letterSpacing: "0.06em", color: t.text3 }}>{children}</Typography>
    <Box sx={{ flex: 1, height: "1px", bgcolor: t.border }} />
  </Box>
);
Joiner.propTypes = { children: PropTypes.node.isRequired };

export default function ConditionEditor({ groups, groupLogic, onChange, fields, loadOperators, emptyText }) {
  const emit = (patch) => onChange({ groups, groupLogic, ...patch });
  const setGroup = (id, updates) => emit({ groups: groups.map((g) => (g.id === id ? { ...g, ...updates } : g)) });
  const addGroup = () => emit({ groups: [...groups, newGroup(groups.length + 1)] });

  if (!groups.length) {
    return (
      <Box sx={{ p: 2, borderRadius: "10px", border: `1px dashed ${t.border2}`, textAlign: "center" }}>
        <Typography sx={{ fontSize: 14, color: t.text2, mb: 1.25 }}>{emptyText}</Typography>
        <Button variant="outlined" size="small" startIcon={<Plus size={15} />} onClick={addGroup}>
          Add a condition
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      {groups.length > 1 && (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
          <Typography sx={{ fontSize: 13.5, color: t.text2 }}>Match groups</Typography>
          <MatchSwitch label="Groups that must match" value={groupLogic || "AND"} onChange={(v) => emit({ groupLogic: v })} />
        </Box>
      )}
      {groups.map((group, gi) => (
        <Box key={group.id}>
          {gi > 0 && <Joiner>{(groupLogic || "AND") === "AND" ? "AND" : "OR"}</Joiner>}
          <Box component="section" aria-label={`Condition group ${gi + 1}`} sx={{ p: 1.25, borderRadius: "12px", bgcolor: t.panel, border: `1px solid ${t.border}`, display: "flex", flexDirection: "column", gap: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 0.25 }}>
              <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: t.text, flex: 1 }}>{groups.length > 1 ? `Group ${gi + 1}` : "Conditions"}</Typography>
              {group.conditions.length > 1 && (
                <>
                  <Typography sx={{ fontSize: 13, color: t.text2 }}>Match</Typography>
                  <MatchSwitch label={`Conditions in group ${gi + 1} that must match`} value={group.logic || "AND"} onChange={(v) => setGroup(group.id, { logic: v, logicMode: "simple" })} />
                </>
              )}
              <Tooltip title={groups.length > 1 ? "Remove group" : "Remove all conditions"}>
                <IconButton size="small" aria-label={`Remove group ${gi + 1}`} onClick={() => emit({ groups: groups.filter((g) => g.id !== group.id) })} sx={{ color: t.text3, "&:hover": { color: v2Tones.danger.fg, bgcolor: v2Tones.danger.bg } }}>
                  <Trash2 size={15} />
                </IconButton>
              </Tooltip>
            </Box>
            {group.conditions.map((c, ci) => (
              <Box key={c.id}>
                {ci > 0 && <Joiner>{(group.logic || "AND") === "AND" ? "AND" : "OR"}</Joiner>}
                <ConditionRow
                  index={ci}
                  condition={c}
                  fields={fields}
                  loadOperators={loadOperators}
                  onUpdate={(updates) => setGroup(group.id, { conditions: group.conditions.map((x) => (x.id === c.id ? { ...x, ...updates } : x)) })}
                  onRemove={() => setGroup(group.id, { conditions: group.conditions.filter((x) => x.id !== c.id) })}
                />
              </Box>
            ))}
            {group.logicMode === "advanced" && group.conditions.length > 1 && (
              <LabeledField
                label="Custom logic"
                value={group.customLogic}
                onChange={(e) => setGroup(group.id, { customLogic: e.target.value })}
                helperText="Condition numbers with AND / OR, for example (1 AND 2) OR 3."
              />
            )}
            <Button size="small" startIcon={<Plus size={15} />} onClick={() => setGroup(group.id, { conditions: [...group.conditions, newCondition()] })} sx={{ alignSelf: "flex-start" }}>
              Add condition
            </Button>
          </Box>
        </Box>
      ))}
      <Button size="small" variant="outlined" startIcon={<Plus size={15} />} onClick={addGroup} sx={{ alignSelf: "flex-start" }}>
        Add group
      </Button>
    </Box>
  );
}

ConditionEditor.propTypes = {
  groups: PropTypes.arrayOf(PropTypes.object).isRequired,
  groupLogic: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  fields: PropTypes.arrayOf(PropTypes.object).isRequired,
  loadOperators: PropTypes.func.isRequired,
  emptyText: PropTypes.string,
};
ConditionEditor.defaultProps = { groupLogic: "AND", emptyText: "No conditions yet." };
